import {
    IIssueResponseBase,
    IssueHardwareHealthResponse,
    IssueMachineHealthResponse,
    IssueStatusType,
} from '@kcftech/prometheus-client';
import { createContext, ReactNode, useContext, useMemo } from 'react';
import { isNodeInactive } from 'utils/node';
import { IssueMode } from '../shared';

export type IssueDetailsContext = Readonly<{
    issue: IIssueResponseBase;
    groupUnlinked: boolean;
    hardwareLocationMoved: boolean | undefined;
    nodeInactive: boolean | undefined;
    isInternal: boolean | undefined;
    isDraft: boolean;
    issueType: IssueMode;
}>;

function buildIssueDetailsContext(issue: IIssueResponseBase): IssueDetailsContext {
    return {
        issue,
        groupUnlinked:
            issue instanceof IssueMachineHealthResponse && issue.Group === undefined,
        hardwareLocationMoved:
            issue instanceof IssueHardwareHealthResponse && issue.HardwareLocationMoved,
        nodeInactive:
            issue instanceof IssueHardwareHealthResponse &&
            issue.Node &&
            isNodeInactive(issue.Node.NodeStatusCode),
        isInternal: issue instanceof IssueHardwareHealthResponse && issue.IsInternal,
        isDraft: issue.IssueStatus.StatusType === IssueStatusType.Draft,
        issueType:
            issue instanceof IssueHardwareHealthResponse
                ? 'hardwareHealth'
                : 'machineHealth',
    };
}

const Context = createContext<IssueDetailsContext | undefined>(undefined);

export type IssueDetailsProviderProps = Readonly<{
    issue: IIssueResponseBase;
    children?: ReactNode;
}>;

export function IssueDetailsProvider({ issue, children }: IssueDetailsProviderProps) {
    const value: IssueDetailsContext = useMemo(
        () => buildIssueDetailsContext(issue),
        [issue],
    );

    return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useIssueDetailsContext(): IssueDetailsContext {
    const ctx = useContext(Context);

    if (!ctx) {
        throw new Error('Missing <IssueDetailsProvider>');
    }

    return ctx;
}
